/**
 * How a pop-up menu is dismissed (spec §8.6 item 5).
 *
 * Before this existed each menu closed only on `onMouseLeave` of its own panel, so a menu opened
 * without the pointer ever entering it stayed open for good, and two could be up at once. One hook
 * now gives every menu the same four exits:
 *
 *  - the pointer leaves the keep-open region of `popoverRegion.ts` (two phases, so a slip off the
 *    edge is forgiven and the gap between button and panel is covered);
 *  - a press anywhere outside the panel, which still reaches whatever is under it — dismissing a
 *    menu costs no click. A press on the opening button is left to that button's own toggle, or the
 *    menu would close here and reopen there;
 *  - Escape, consumed so it does not also clear the block selection (§8.18);
 *  - another menu opening: a module-level registry keeps exactly one open.
 *
 * The relation rail's panel (§8.15) deliberately does not use this.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { contains, keepOpenRegion, type Rect } from "./popoverRegion";

/** The one open menu, if any. A module singleton: menus live inside mirrors and in the page header
 *  alike, and this needs no provider threaded through either. */
let current: { token: object; close: () => void } | null = null;

export interface Popover<P extends HTMLElement> {
  open: boolean;
  /** for the opening button's `onClick`; the event tells a keyboard activation from a click */
  toggle: (e?: { detail?: number }) => void;
  close: () => void;
  buttonRef: React.RefObject<HTMLButtonElement | null>;
  /** the panel element; the menu is a `<div>` unless a caller says otherwise */
  panelRef: React.RefObject<P | null>;
}

const rectOf = (el: Element | null): Rect | null => el?.getBoundingClientRect() ?? null;

export function usePopover<P extends HTMLElement = HTMLDivElement>(): Popover<P> {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<P | null>(null);
  // the pointer has been inside the panel at least once: the region shrinks to the panel
  const entered = useRef(false);
  // a menu opened from the keyboard is not hover-dismissed until the pointer has been inside it,
  // so a menu the mouse never approached does not vanish on a stray movement
  const hoverArmed = useRef(true);

  // identity of this menu in the registry (a ref, so `close` need not refer to itself)
  const token = useRef({});
  const close = useCallback(() => {
    setOpen(false);
    if (current?.token === token.current) current = null;
  }, []);

  const toggle = useCallback(
    (e?: { detail?: number }) => {
      setOpen((wasOpen) => {
        if (wasOpen) {
          if (current?.token === token.current) current = null;
          return false;
        }
        if (current && current.token !== token.current) current.close(); // one at a time
        current = { token: token.current, close };
        entered.current = false;
        hoverArmed.current = e?.detail !== 0; // detail 0 = activated by the keyboard
        return true;
      });
    },
    [close],
  );

  useEffect(() => {
    if (!open) return;

    const onPointerMove = (e: PointerEvent) => {
      const panel = rectOf(panelRef.current);
      if (!panel) return;
      if (contains(panel, e.clientX, e.clientY)) {
        entered.current = true;
        hoverArmed.current = true;
        return;
      }
      if (!hoverArmed.current) return;
      if (!contains(keepOpenRegion(panel, rectOf(buttonRef.current), entered.current), e.clientX, e.clientY)) close();
    };

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return; // the button's own onClick toggles
      close();
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation(); // not also the block selection (§8.18)
      e.preventDefault();
      close();
      buttonRef.current?.focus();
    };

    window.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown, { capture: true });
    };
  }, [open, close]);

  // a menu whose block is deleted or scrolled out of the virtualiser must not hold the registry
  useEffect(() => {
    const mine = token.current;
    return () => {
      if (current?.token === mine) current = null;
    };
  }, []);

  return { open, toggle, close, buttonRef, panelRef };
}
